import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Disclosure, Typography, type TypographyProps } from '@heroui/react';
import type { ApiErrorDebug } from '@/api/handlers/responses';
import { env } from '@/config/env';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ApiErrorDetailsProps = {
    /** Anything a surface caught; only an API error with `debug` shows. */
    error: unknown;
    id: string;
};

type ExplainedApiError = {
    debug: ApiErrorDebug;
    correlationId?: string;
};

/**
 * The API's explanation of a 500, when `error` carries one: an `ErrorResponse`
 * whose `debug` a Development-mode bos-api sent. `null` for anything else.
 */
export const getExplainedApiError = (
    error: unknown,
): ExplainedApiError | null => {
    if (typeof error !== 'object' || error === null || !('debug' in error)) {
        return null;
    }

    const { debug, correlationId } = error as {
        debug?: ApiErrorDebug;
        correlationId?: string;
    };

    if (!debug || typeof debug.type !== 'string') {
        return null;
    }

    return { debug, correlationId };
};

// A `span`, since a `p` isn't allowed inside the trigger button.
const asSpan: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <span {...domProps}>{children}</span>;

/**
 * Collapsed technical details for a server exception (BOS-236): the API's
 * mode, the exception type and message, the correlation id, the stack trace
 * and the inner exceptions. Development builds only; a production build
 * renders nothing, whatever the error carries.
 */
export const ApiErrorDetails: React.FC<ApiErrorDetailsProps> = ({
    error,
    id,
}) => {
    const { t } = useTranslation('components');

    if (!env.isDevelopmentBuild) {
        return null;
    }

    const explained = getExplainedApiError(error);

    if (!explained) {
        return null;
    }

    const { debug, correlationId } = explained;

    return (
        <Disclosure
            className="grid w-full grid-cols-[minmax(0,1fr)]"
            {...getNodeTestIdProp(id)}
            id={id}
        >
            <Disclosure.Heading>
                <Disclosure.Trigger
                    className="flex items-center gap-2 py-1"
                    {...getNodeTestIdProp(`${id}-toggle`)}
                >
                    <Disclosure.Indicator className="ms-0" />
                    <Typography render={asSpan} type="body-sm" weight="medium">
                        {t('apiErrorDetails.toggle')}
                    </Typography>
                </Disclosure.Trigger>
            </Disclosure.Heading>
            <Disclosure.Content>
                <Disclosure.Body
                    className="flex flex-col gap-2 pt-1"
                    {...getNodeTestIdProp(`${id}-body`)}
                >
                    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                        <dt className="text-muted">
                            {t('apiErrorDetails.type')}
                        </dt>
                        <dd className="font-mono break-all">{debug.type}</dd>
                        <dt className="text-muted">
                            {t('apiErrorDetails.message')}
                        </dt>
                        <dd className="break-words">{debug.message}</dd>
                        <dt className="text-muted">
                            {t('apiErrorDetails.apiMode')}
                        </dt>
                        <dd className="font-mono">{debug.mode}</dd>
                        {correlationId ? (
                            <>
                                <dt className="text-muted">
                                    {t('apiErrorDetails.correlationId')}
                                </dt>
                                <dd className="font-mono break-all">
                                    {correlationId}
                                </dd>
                            </>
                        ) : null}
                    </dl>
                    {debug.innerExceptions.length > 0 ? (
                        <div className="flex flex-col gap-1 text-xs">
                            <span className="text-muted">
                                {t('apiErrorDetails.innerExceptions')}
                            </span>
                            <ol className="list-decimal ps-5">
                                {debug.innerExceptions.map((inner, index) => (
                                    <li
                                        className="break-words"
                                        key={`${index}-${inner.type}`}
                                    >
                                        <span className="font-mono">
                                            {inner.type}
                                        </span>
                                        : {inner.message}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ) : null}
                    <div className="flex flex-col gap-1 text-xs">
                        <span className="text-muted">
                            {t('apiErrorDetails.stackTrace')}
                        </span>
                        <pre
                            className="max-h-64 overflow-auto rounded-md bg-surface-secondary p-2 font-mono text-[11px] leading-snug whitespace-pre"
                            {...getNodeTestIdProp(`${id}-stack`)}
                        >
                            {debug.stackTrace}
                        </pre>
                    </div>
                </Disclosure.Body>
            </Disclosure.Content>
        </Disclosure>
    );
};
